<template>
  <div class="m-paymentMethod" v-on:click="toggleCreditCard">
    <div class="m-paymentMethod--column">
      <div class="m-paymentMethod--rowSpaceBetween">
        <div class="m-paymentMethod--row">
          <div class="a-input-radio">
            <input id="m-paymentMethod--creditcard" name="paymenttoken" class="a-input-radio__input" value="creditcard" type="radio" />
            <div class="a-input-radio__check"></div>
          </div>
          <img v-bind:src="paymentsAssetUrl.visa"  class="pay-logo visa"/>
          <img v-bind:src="paymentsAssetUrl.mastercard"  class="pay-logo mc"/>
        </div>
        <i id="checkout-creditcard-dropdown" :class="['far', showCreditCard ? 'fa-angle-up' : 'fa-angle-down']" aria-hidden="true"></i>
      </div>
      <form
        v-show="showCreditCard"
        v-on:submit="tokenizeCreditCard($event)"
        class="m-creditCard--form">
        <div class="a-input-text a-input-text-div__m-creditCard-number">
          <label class="a-input-text__label m-creditCard-number-label" for="m-creditCard-number">Card Number*</label>
          <div id="m-creditCard-number" class="a-input-text__input"></div>
          <p v-if="!isValidCardNumbers" class="a-input__error-message">Invalid Card number</p>
        </div>
        <div class="m-creditCard--form__row">
          <div class="a-input-text a-input-text-div__m-creditCard-date">
            <label class="a-input-text__label m-creditCard-date-label" for="m-creditCard-date">Expiration date*</label>
            <div id="m-creditCard-date" class="a-input-text__input"></div>
            <p v-if="!isValidCardDate" class="a-input__error-message">Invalid date</p>
          </div>
          <div class="a-input-text a-input-text-div__m-creditCard-cvv">
            <label class="a-input-text__label m-creditCard-cvv-label" for="m-creditCard-cvv">CVV*</label>
            <div id="m-creditCard-cvv" class="a-input-text__input"></div>
            <p v-if="!isValidCardCVV" class="a-input__error-message">Invalid cvv</p>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
/**
 * This component displays a credit card form using the `braintree-web/hosted-fields`
 * library.
 *
 * This library works for targeting <div> elements on the web page and replacing them
 * with IFRAMEs that host form elements served directly from Braintree's servers.
 *
 * This absolves us of any PCI compliance concerns whilst still giving us reasonable
 * control over look and feel.
 *
 * Braintree JS SDK v3 PayPal dev guide:
 * https://developers.braintreepayments.com/guides/paypal/overview/javascript/v3
 */
import HostedFields from 'braintree-web/hosted-fields'
import { mapState } from 'vuex'
import InputText from '../atoms/InputText.vue'

export default {
  computed: mapState({
    paymentsAssetUrl: state => state.globalstate.paymentsAssetUrl,
    storedPaymentMethods: state => state.globalstate.paymentGatewayInfo.storedPaymentMethods,
    paymentGatewayInfo: state => state.globalstate.paymentGatewayInfo
  }),
  components: { InputText },
  props: ['clientInstance'],
  mounted () {
    this.createHostedFields()
    // If user has no stored payment method and not coming from My Serato,
    // select credit card by default
    if (!this.storedPaymentMethods || this.storedPaymentMethods.length === 0) {
      this.expandCreditCard()
    }
  },
  provide () {
    return { parentValidator: this.$validator }
  },
  data () {
    return {
      isValidCard: true,
      isValidCardNumbers: true,
      isValidCardDate: true,
      isValidCardCVV: true,
      showCreditCard: false
    }
  },
  methods: {
    expandCreditCard () {
      this.showCreditCard = true
      const element = document.getElementById('m-paymentMethod--creditcard')
      element.checked = true
    },
    collapseCreditCard () {
      this.showCreditCard = false
    },
    toggleCreditCard (event) {
      const element = document.getElementById('m-paymentMethod--creditcard').parentElement
      // Let's not collapse this if you manage to click outside a control but inside the expanded LI
      if ((event.clientY - element.getBoundingClientRect().top) <= 50) {
        if (this.showCreditCard) {
          this.collapseCreditCard()
        } else {
          this.expandCreditCard()
          this.$emit('openCreditCard')
        }
      }
    },
    tokenizeCreditCard (event) {
      if (event) event.preventDefault()
      return new Promise((resolve, reject) => {
        this.hostedFieldsInstance.tokenize((err, payload) => {
          if (err) {
            reject(err)
          } else {
            resolve(payload.nonce)
          }
        })
      })
    },
    createHostedFields () {
      HostedFields.create(
        {
          client: this.clientInstance,
          fields: {
            number: { selector: '#m-creditCard-number' },
            expirationDate: { selector: '#m-creditCard-date' },
            cvv: { selector: '#m-creditCard-cvv' }
          },
          styles: {
            'input': {
              'font-size': '1em',
              'font-family': 'HelveticaNeueW01-55Roma,Helvetica,Arial,sans-serif'
            }
          }
        },
        (err, hostedFieldsInstance) => {
          if (err) {
            // console.log(err)
            return
          }
          hostedFieldsInstance.on('validityChange', (event) => {
            this.isValidCard = this.isValidCardNumbers = this.isValidCardDate = this.isValidCardCVV = true
            Object.keys(event.fields).forEach((key) => {
              const field = event.fields[key]
              if (!field.isValid) {
                this.isValidCard = false
                const hasInvalidClass = field.container.classList.contains('braintree-hosted-fields-invalid')
                if (!field.isEmpty && hasInvalidClass) {
                  switch (field.container.id) {
                    case 'm-creditCard-number':
                      this.isValidCardNumbers = false
                      break
                    case 'm-creditCard-date':
                      this.isValidCardDate = false
                      break
                    case 'm-creditCard-cvv':
                      this.isValidCardCVV = false
                      break
                  }
                }
              }
            })
          })
          this.hostedFieldsInstance = hostedFieldsInstance
        }
      )
    },
    teardownHostedFields () {
      this.hostedFieldsInstance.teardown((err) => {
        if (err) {
          // console.log(err)
        } else {
          this.hostedFieldsInstance = ''
        }
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// CreditCard.vue?05ff55d9